import { useEffect, useRef, useState } from 'react';

const SCRIPT_ID = 'cloudflare-turnstile-script';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const DEVELOPMENT_SITE_KEY = '1x00000000000000000000AA';

let turnstileScriptPromise;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    const script = existing || document.createElement('script');
    const handleLoad = () => window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile unavailable'));
    const handleError = () => reject(new Error('Turnstile failed to load'));

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    if (!existing) {
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
  return turnstileScriptPromise;
}

export default function TurnstileWidget({ action, onToken, resetSignal = 0 }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loadError, setLoadError] = useState(false);
  const configuredSiteKey = String(process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
  const siteKey = configuredSiteKey || (process.env.NODE_ENV !== 'production' ? DEVELOPMENT_SITE_KEY : '');

  useEffect(() => {
    let cancelled = false;
    setLoadError(false);
    onToken('');

    if (!siteKey) {
      setLoadError(true);
      return undefined;
    }

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          size: 'flexible',
          theme: 'light',
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'timeout-callback': () => onToken(''),
          'error-callback': () => {
            onToken('');
            setLoadError(true);
          },
        });
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // The widget may already have expired and removed itself.
        }
      }
      widgetIdRef.current = null;
    };
  }, [action, onToken, siteKey]);

  useEffect(() => {
    if (!resetSignal || widgetIdRef.current === null || !window.turnstile) return;
    try {
      window.turnstile.reset(widgetIdRef.current);
      onToken('');
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, [onToken, resetSignal]);

  return (
    <div className="soagent-turnstile" aria-label="Security verification">
      <div ref={containerRef} />
      {loadError && <p role="alert">Security check unavailable. Refresh and try again.</p>}
    </div>
  );
}
